import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../services/auth';
import api from '../services/api';
import { identify, track, reset } from '../services/analytics';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadUser = useCallback(async () => {
    const token = localStorage.getItem('inveira_token');
    if (!token) { setLoading(false); return; }
    try {
      // Always re-issue the JWT so role/plan from the DB are current —
      // prevents stale-token bugs after Stripe plan changes.
      const res = await api.post('/auth/refresh');
      localStorage.setItem('inveira_token', res.data.token);
      setUser(res.data.user);
    } catch {
      localStorage.removeItem('inveira_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Wake up the Render backend before the user starts an interview
    const backendUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    fetch(`${backendUrl}/ping`).catch(() => {});
    loadUser();
  }, [loadUser]);

  const login = async (email, password) => {
    const res = await authService.login({ email, password });
    if (res.data.requires2FA) return { requires2FA: true, userId: res.data.userId };
    localStorage.setItem('inveira_token', res.data.token);
    setUser(res.data.user);
    identify(res.data.user.id, { email: res.data.user.email });
    track('login', { method: 'email' });
    return { success: true };
  };

  const signup = async (email, password, name, dataSharingConsent = false) => {
    const res = await authService.signup({ email, password, name, dataSharingConsent });
    if (res.data.token) {
      localStorage.setItem('inveira_token', res.data.token);
      setUser(res.data.user);
      identify(res.data.user.id, { email: res.data.user.email, name });
      track('signup', { method: 'email', data_sharing_consent: dataSharingConsent });
    }
    return res.data;
  };

  const verify2FA = async (userId, token) => {
    const res = await authService.verify2FA({ userId, token });
    localStorage.setItem('inveira_token', res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const logout = async () => {
    try { await authService.logout(); } catch {}
    localStorage.removeItem('inveira_token');
    setUser(null);
    reset();
  };

  const updateUser = (updates) => setUser(prev => ({ ...prev, ...updates }));

  return (
    <AuthContext.Provider value={{ user, loading, login, signup, logout, verify2FA, updateUser, loadUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
